<template>
  <div>
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <DynamicPopup />
    <DynamicSidePanel />
    <UISnackBanner />
    <SupportOpenAgentInfo v-if="showSupportFloaty" />
  </div>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import { USER_ROLES } from '~/constants';
import { DEFAULT_LOCALE } from '~/constants/i18n';
import { getLocaleMeta, normalizeLocale } from '~/utils/i18n/locale';
import { shouldShowSupportFloaty } from '~/utils/supportFloaty';

const authStore = useAuthStore();

const { catchErrors } = useErrorHandler();

const { refetch: refetchUser, error: userError } = useCurrentUser();

const { refetch: refetchAgent, error: agentError } = useAgent(false);

const route = useRoute();

const { locale } = useI18n();
const localeMeta = computed(() => getLocaleMeta(normalizeLocale(locale.value) ?? DEFAULT_LOCALE));
useHead({
  htmlAttrs: {
    lang: computed(() => localeMeta.value.language),
    dir: computed(() => localeMeta.value.dir),
  },
});
// Canonical + hreflang alternates on localized routes; translated site-wide meta defaults.
useLocalizedSeo();

// Signed-in portal only. Decided after mount: SWR-cached pages render signed-out on the server,
// so gating the SSR output on the token would mismatch on hydration.
const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});
const showSupportFloaty = computed(
  () => isMounted.value && shouldShowSupportFloaty({ path: route.path, isSignedIn: !!authStore.getToken }),
);

if (import.meta.client) {
  const { getBclickId } = useBclickId();
  getBclickId();
}

const initUser = async () => {
  await refetchUser();

  if (userError.value) {
    catchErrors(userError.value);
    return;
  }
  if (!authStore.getUser) return;

  Sentry.setUser({
    id: authStore.getUser.id,
    name: authStore.getUser.firstName,
    email: authStore.getUser.email,
    role: authStore.getUser.role,
  });

  try {
    // Using bracket notation to avoid TypeScript errors since clarity is not defined in Window type
    const clarityFn = (window as any)['clarity'];
    if (typeof clarityFn === 'function') {
      clarityFn('identify', authStore.getUser.email);
    }
  } catch (e) {}
};

watch(
  () => route.name,
  (_, oldValue) => {
    if (!authStore.getUser || !authStore.getToken) return;
    if (oldValue?.toString().includes('login-auto')) {
      initUser();
      refetchAgent();
    }
  },
);

watch(
  () => authStore.getToken,
  async () => {
    if (route.name?.toString().includes('login-auto')) return;
    await initUser();
  },
  { immediate: true },
);

watch(
  () => authStore.getUser,
  async (user) => {
    if (route.name?.toString().includes('login-auto')) return;
    if (!user) return;
    if (user.role !== USER_ROLES.LEAD && user.role !== USER_ROLES.ADMIN) return;

    await refetchAgent();
    if (agentError.value) {
      catchErrors(agentError.value);
      return;
    }
  },
  { immediate: true, deep: true },
);

const { mutateAsync: updateTimezone } = useUpdateTimezone();

watch(
  () => authStore.getUser,
  async () => {
    if (route.name?.toString().includes('login-auto')) return;
    if (!authStore.getUser || !authStore.getToken) return;
    // only on client side
    if (typeof window === 'undefined') return;

    updateTimezone({
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).catch((error) => {
      catchErrors(error);
    });
  },
  {
    immediate: true,
  },
);
</script>
